import { useQuery } from "convex/react";
import { api } from "../../api";
import { type GenericId as Id } from "convex/values";
import { useEffect, useMemo } from "react";

export const useNetworkRuleDetail = (ruleId: string | null) => {
  const ruleDetail = useQuery(
    api.rules.get.rules,
    ruleId ? { rules: [ruleId as Id<"rules">] } : "skip"
  );

  // Log the raw response for debugging
  useEffect(() => {
    if (ruleId) {
      console.log('=== Network Rule Detail Debug ===');
      console.log('Rule ID requested:', ruleId);
      console.log('Raw API response (full array):', ruleDetail);
      console.log('Extracted rule data (first item):', ruleDetail?.[0]);
      console.log('================================');
    }
  }, [ruleId, ruleDetail]);

  // Transform raw Convex data to match NetworkRule interface
  const transformedData = useMemo(() => {
    const rawRule = ruleDetail?.[0];
    if (!rawRule) return undefined;

    // Cast to access potential _id and _creationTime from Convex
    const convexRule = rawRule as typeof rawRule & { _id?: string; _creationTime?: number };

    // Use ruleId as fallback since we know it from the query parameter
    const extractedId = convexRule._id || ruleId || '';
    console.log('ID extraction - _id:', convexRule._id, 'ruleId fallback:', ruleId, 'final:', extractedId);

    return {
      ...rawRule,
      id: extractedId,  // Critical: Map _id to id with ruleId fallback
      name: rawRule.name || 'Unnamed Rule',
      urls: rawRule.urls || [],
      apps: [],
      lastModified: new Date(rawRule.lastCheck || convexRule._creationTime || Date.now())
        .toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        }),
      type: rawRule.parent ? "App Specific" : "General",
      category: rawRule.parent ? "app-specific" : "system-wide",
      ruleType: 'webkit',
      scope: {
        users: 0,
        devices: 0,
        groups: []
      }
    };
  }, [ruleDetail, ruleId]);  // Added ruleId as dependency for fallback

  return {
    data: transformedData,
    isLoading: ruleDetail === undefined,
  };
};
